import { type CampaignCreationRequestStatus, isPrivilegedRole } from "@adpilot/shared";
import {
  IconAlertTriangle,
  IconArrowRight,
  IconBrandAmazon,
  IconCheck,
  IconClock,
  IconPlayerPlay,
  IconRefresh,
  IconRosetteDiscountCheck,
  IconTool,
  IconX,
} from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { HTTPError } from "ky";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { AMAZON_ADS_MESSAGE_TYPE } from "@/lib/amazon-ads-oauth";
import { fetchAmazonAccounts, reauthorizeAccount } from "@/lib/amazon-api";
import { getApiErrorCode, getApiErrorMessage } from "@/lib/api-error";
import { fetchCampaignCreationStatus, resumeCampaignCreation } from "@/lib/campaign-creation-api";
import {
  locateCampaignRequestShop,
  rememberCampaignRequestShop,
} from "@/lib/campaign-creation-session";
import {
  classifyCampaignCreationResult,
  hasCampaignCreationAuthorizationFailure,
} from "@/lib/campaign-creation-state";
import { emitCampaignCreationTelemetry } from "@/lib/campaign-creation-telemetry";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/stores/auth";

const SP_STEP_NAMES = [
  "campaign",
  "ad_group",
  "product_ads",
  "target_bids",
  "negatives",
  "local_sync",
] as const;

export function CampaignCreationResult({ requestId }: { requestId: string }) {
  const { t } = useTranslation("campaignCreation");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);
  const setCurrentShop = useAuthStore((state) => state.setCurrentShop);
  const workspaceId = user?.activeWorkspaceId ?? null;
  const located = workspaceId
    ? locateCampaignRequestShop(sessionStorage, workspaceId, requestId)
    : null;
  const [shopId, setShopId] = useState<string | null>(located);
  const [authorizationRecovered, setAuthorizationRecovered] = useState(false);
  const terminalTelemetryRef = useRef<string | null>(null);

  const accounts = useQuery({
    queryKey: ["amazon", "accounts", workspaceId],
    queryFn: fetchAmazonAccounts,
    enabled: Boolean(workspaceId && !shopId),
  });
  const profiles = useMemo(
    () =>
      (accounts.data?.connections.flatMap((connection) => connection.shops) ?? []).filter(
        (profile) =>
          profile.workspaceId === workspaceId &&
          profile.isActive &&
          profile.status === "connected" &&
          isPrivilegedRole(profile.role),
      ),
    [accounts.data, workspaceId],
  );

  const statusQuery = useQuery({
    queryKey: ["campaign-creation", "request", shopId, requestId],
    queryFn: () => {
      if (!shopId) throw new Error("Campaign creation request requires a Shop");
      return fetchCampaignCreationStatus(requestId, shopId);
    },
    enabled: Boolean(shopId),
    retry: false,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === "pending" || status === "processing" ? 1500 : false;
    },
  });

  const resume = useMutation({
    mutationFn: () => {
      if (!shopId) throw new Error("Campaign creation request requires a Shop");
      return resumeCampaignCreation(requestId, shopId);
    },
    onSuccess: (status) =>
      queryClient.setQueryData(["campaign-creation", "request", shopId, requestId], status),
    onError: async (error) => {
      if ((await getApiErrorCode(error)) === "campaign_creation.authorization_invalid") {
        setAuthorizationRecovered(false);
        toast.error(await getApiErrorMessage(error));
        return;
      }
      toast.error(t("result.transportError"));
      void statusQuery.refetch();
    },
  });
  const reauthorize = useMutation({
    mutationFn: () => {
      if (!shopId) throw new Error("Amazon reauthorization requires a Shop");
      return reauthorizeAccount(shopId);
    },
    onSuccess: ({ redirectUrl }) => {
      const popup = window.open(redirectUrl, "amazon-reauthorize", "width=600,height=700");
      if (!popup) window.location.href = redirectUrl;
    },
    onError: async (error) => toast.error(await getApiErrorMessage(error)),
  });

  // A signal, not a payload: nothing is read off the message, so the origin
  // check carries it, and the recovered flag it sets is re-checked server-side.
  // Deliberately not pinned to a popup — see sync-alerts.
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== AMAZON_ADS_MESSAGE_TYPE || !event.data?.success) return;
      setAuthorizationRecovered(true);
      void queryClient.invalidateQueries({ queryKey: ["amazon", "accounts", workspaceId] });
      void queryClient.invalidateQueries({
        queryKey: ["campaign-creation", "capabilities", shopId],
      });
      toast.success(t("result.authorizationRecovered"));
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [queryClient, shopId, t, workspaceId]);

  const isNotFound =
    statusQuery.error instanceof HTTPError && statusQuery.error.response.status === 404;
  const status = statusQuery.data;
  const kind = status ? classifyCampaignCreationResult(status) : null;
  const authorizationFailure = status ? hasCampaignCreationAuthorizationFailure(status) : false;
  useEffect(() => {
    if (!status || !kind || kind === "processing") return;
    const normalized = kind === "reconcile" ? "unknown" : status.status;
    if (normalized === "pending" || normalized === "processing") return;
    const terminalKey = `${status.requestId}:${normalized}`;
    if (terminalTelemetryRef.current === terminalKey) return;
    terminalTelemetryRef.current = terminalKey;
    emitCampaignCreationTelemetry({
      name: "terminal_result",
      step: 3,
      status: normalized,
      failedStep:
        status.resumableFrom ??
        status.steps.find((step) => step.status === "failed" || step.status === "unknown")?.step,
    });
  }, [kind, status]);

  if (!shopId) {
    const items = [
      { value: null, label: t("profile.placeholder") },
      ...profiles.map((profile) => ({
        value: profile.shopId,
        label: `${profile.shopName} · ${profile.marketplaceName}`,
      })),
    ];
    return (
      <ResultFrame>
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <IconRefresh />
            </EmptyMedia>
            <EmptyTitle>{t("result.selectProfile.title")}</EmptyTitle>
            <EmptyDescription>{t("result.selectProfile.description")}</EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            <Select
              items={items}
              value={null}
              onValueChange={(value) => {
                if (!value || !workspaceId) return;
                setCurrentShop(value);
                setShopId(value);
                rememberCampaignRequestShop(sessionStorage, workspaceId, requestId, value);
              }}
            >
              <SelectTrigger className="w-80">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {items.map((item) => (
                    <SelectItem key={item.value ?? "empty"} value={item.value}>
                      {item.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </EmptyContent>
        </Empty>
      </ResultFrame>
    );
  }

  if (statusQuery.isLoading) {
    return (
      <ResultFrame>
        <CampaignCreationProgress status={status} isMutationPending />
      </ResultFrame>
    );
  }
  if (isNotFound) {
    return (
      <ResultFrame>
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <IconAlertTriangle />
            </EmptyMedia>
            <EmptyTitle>{t("result.notFound.title")}</EmptyTitle>
            <EmptyDescription>{t("result.notFound.description")}</EmptyDescription>
          </EmptyHeader>
          <EmptyContent className="flex gap-2">
            <Button variant="outline" onClick={() => void navigate({ to: "/campaigns" })}>
              {t("actions.backToCampaigns")}
            </Button>
          </EmptyContent>
        </Empty>
      </ResultFrame>
    );
  }
  if (statusQuery.isError || !status || !kind) {
    return (
      <ResultFrame>
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <IconX />
            </EmptyMedia>
            <EmptyTitle>{t("result.loadFailed.title")}</EmptyTitle>
            <EmptyDescription>{t("result.loadFailed.description")}</EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            <Button onClick={() => void statusQuery.refetch()}>{t("actions.retry")}</Button>
          </EmptyContent>
        </Empty>
      </ResultFrame>
    );
  }
  if (kind === "processing")
    return (
      <ResultFrame adProduct={status.adProduct}>
        <CampaignCreationProgress status={status} isMutationPending={resume.isPending} />
      </ResultFrame>
    );

  return (
    <ResultFrame adProduct={status.adProduct}>
      <ResultSummary
        kind={kind}
        status={status}
        action={
          authorizationFailure && !authorizationRecovered ? (
            <Button disabled={reauthorize.isPending} onClick={() => reauthorize.mutate()}>
              {reauthorize.isPending ? (
                <Spinner data-icon="inline-start" />
              ) : (
                <IconBrandAmazon data-icon="inline-start" />
              )}
              {t("result.reconnectAmazon")}
            </Button>
          ) : kind === "succeeded" && status.localCampaignId ? (
            <Button
              render={<Link to="/campaigns/$id" params={{ id: status.localCampaignId }} />}
              onClick={() => {
                setCurrentShop(shopId);
                emitCampaignCreationTelemetry({ name: "campaign_detail_click", step: 3 });
              }}
            >
              {t("result.openCampaign")}
              <IconArrowRight data-icon="inline-end" />
            </Button>
          ) : kind === "resumable" || kind === "paused" ? (
            <Button
              disabled={resume.isPending}
              onClick={() => {
                emitCampaignCreationTelemetry({ name: "resume_click", step: 3 });
                resume.mutate();
              }}
            >
              {resume.isPending ? (
                <Spinner data-icon="inline-start" />
              ) : (
                <IconPlayerPlay data-icon="inline-start" />
              )}
              {t("result.continue")}
            </Button>
          ) : kind === "edit" ? (
            <Button onClick={() => void navigate({ to: "/campaigns/new" })}>
              {t("result.editAndRetry")}
            </Button>
          ) : null
        }
      />
    </ResultFrame>
  );
}

export function ResultFrame({
  children,
  adProduct,
}: {
  children: React.ReactNode;
  adProduct?: CampaignCreationRequestStatus["adProduct"];
}) {
  const { t } = useTranslation("campaignCreation");
  return (
    <main className="mx-auto flex min-h-full w-full min-w-[960px] max-w-5xl flex-col gap-5 px-6 py-8">
      <div>
        <p className="mb-1 text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground">
          {adProduct ? t(`result.productEyebrow.${adProduct}`) : t("eyebrow")}
        </p>
        <h1 className="text-2xl font-semibold tracking-tight">{t("result.title")}</h1>
      </div>
      {children}
    </main>
  );
}

export function CampaignCreationProgress({
  status,
  isMutationPending,
}: {
  status?: CampaignCreationRequestStatus;
  isMutationPending: boolean;
}) {
  const { t } = useTranslation("campaignCreation");
  const stepNames =
    status && status.adProduct !== "sp"
      ? status.steps.map((step) => step.step)
      : [...SP_STEP_NAMES];
  const succeeded = stepNames.filter(
    (step) => status?.steps.find((candidate) => candidate.step === step)?.status === "succeeded",
  ).length;
  const progress = Math.round((succeeded / Math.max(stepNames.length, 1)) * 100);
  const activeIndex = stepNames.findIndex(
    (step) => status?.steps.find((candidate) => candidate.step === step)?.status !== "succeeded",
  );
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {isMutationPending ? <Spinner /> : <IconClock />}
          {t("result.processing.title")}
        </CardTitle>
        <CardDescription>{t("result.processing.keepOpen")}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-5">
        <Progress value={progress} />
        <ol className="grid grid-cols-2 gap-2">
          {stepNames.map((step, index) => {
            const recorded = status?.steps.find((candidate) => candidate.step === step);
            const complete = recorded?.status === "succeeded";
            const active = !complete && index === activeIndex;
            return (
              <li
                key={step}
                className={cn(
                  "flex items-center gap-3 rounded-lg border border-line p-3 text-xs text-muted-foreground",
                  active && "bg-subtle text-ink",
                )}
              >
                <span
                  className={cn(
                    "grid size-6 place-items-center rounded-full border border-line font-mono text-[10px]",
                    complete && "border-success bg-success text-white",
                  )}
                >
                  {complete ? <IconCheck /> : index + 1}
                </span>
                {t(`result.steps.${step}`)}
              </li>
            );
          })}
        </ol>
      </CardContent>
    </Card>
  );
}

export function ResultSummary({
  kind,
  status,
  action,
}: {
  kind: ReturnType<typeof classifyCampaignCreationResult>;
  status: CampaignCreationRequestStatus;
  action: React.ReactNode;
}) {
  const { t } = useTranslation("campaignCreation");
  const config = {
    succeeded: { icon: IconRosetteDiscountCheck, variant: "success" as const },
    paused: { icon: IconClock, variant: "warning" as const },
    resumable: { icon: IconRefresh, variant: "warning" as const },
    manual_remediation: { icon: IconTool, variant: "warning" as const },
    edit: { icon: IconX, variant: "error" as const },
    reconcile: { icon: IconAlertTriangle, variant: "error" as const },
    processing: { icon: IconClock, variant: "default" as const },
  }[kind];
  const Icon = config.icon;
  const failures = status.steps.flatMap((step) =>
    step.itemFailures.map((failure) => ({ ...failure, step: step.step })),
  );
  const isActivated = status.steps.some(
    (step) => step.step === "activation" && step.status === "succeeded",
  );
  const successGuidanceKey = isActivated
    ? status.adProduct === "sb"
      ? "result.states.succeeded.sbGuidance"
      : status.adProduct === "sd"
        ? "result.states.succeeded.sdGuidance"
        : "result.states.succeeded.guidance"
    : status.adProduct === "sb"
      ? "result.states.succeeded.sbPausedGuidance"
      : status.adProduct === "sd"
        ? "result.states.succeeded.sdPausedGuidance"
        : "result.states.succeeded.guidance";
  return (
    <Card>
      <CardHeader>
        <div className="mb-3 flex size-12 items-center justify-center rounded-full bg-subtle">
          <Icon className="size-6" />
        </div>
        <CardTitle>{t(`result.states.${kind}.title`)}</CardTitle>
        <CardDescription>{t(`result.states.${kind}.description`)}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-5">
        <Alert variant={config.variant}>
          <AlertTitle>
            {kind === "succeeded"
              ? t(isActivated ? "result.activatedDisclosure" : "result.pausedDisclosure")
              : t("result.currentStatus")}
          </AlertTitle>
          <AlertDescription>
            {kind === "succeeded" ? t(successGuidanceKey) : t(`result.states.${kind}.guidance`)}
          </AlertDescription>
        </Alert>
        <div className="grid grid-cols-2 gap-2">
          {status.steps.map((step) => (
            <div
              key={step.step}
              className="flex items-center justify-between rounded-lg border border-line px-3 py-2"
            >
              <span className="text-xs">{t(`result.steps.${step.step}`)}</span>
              <Badge variant="secondary">{t(`result.stepStatus.${step.status}`)}</Badge>
            </div>
          ))}
        </div>
        {failures.length > 0 ? (
          <div>
            <h3 className="mb-2 text-sm font-semibold">{t("result.itemFailures")}</h3>
            <div className="max-h-64 overflow-y-auto rounded-lg border border-line">
              {failures.map((failure) => {
                const reference = status.failureProductReferences.find(
                  (item) => item.payloadIndex === failure.payloadIndex,
                );
                return (
                  <div
                    key={`${failure.step}:${failure.payloadIndex}`}
                    className="border-b border-line p-3 last:border-b-0"
                  >
                    <p className="font-mono text-xs">
                      #{failure.payloadIndex}
                      {reference ? ` · ${reference.sku ?? reference.asin}` : ""}
                    </p>
                    <p className="mt-1 text-[11px] text-muted-foreground">
                      {failure.errorCode}
                      {failure.reasonCode ? ` · ${failure.reasonCode}` : ""}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}
        <div className="flex justify-end">{action}</div>
      </CardContent>
    </Card>
  );
}
